import { createSelector } from 'reselect';

import { AviaPassengerType, PassengerTypeSearchParameter } from '@websky/graphql';
import { AnyStateWithExchange, ExchangeTravellerValue } from '../types';

import { getTravellersValues } from '../../../../../Checkout/store/order/selectors';

export const getPassengersState = (state: AnyStateWithExchange) => state.exchange.passengers;

export const getPassengersIds = createSelector(getPassengersState, Object.keys);

export const getSelectedPassengersIds = createSelector(getPassengersState, passengersState =>
	Object.entries(passengersState).reduce<string[]>(
		(ids, [id, isSelected]) => [...ids, ...(isSelected ? [id] : [])],
		[]
	)
);

export const getIsAllPassengersSelected = createSelector(getPassengersState, passengersState =>
	Object.values(passengersState).every(value => value)
);

export const getPassengers = createSelector(
	getPassengersState,
	getTravellersValues,
	(passengersState, travellersValues) => {
		return travellersValues.map<ExchangeTravellerValue>(traveller => ({
			...traveller,
			isSelected: passengersState[traveller.id]
		}));
	}
);

export const getPassengersForSelection = createSelector(getPassengers, passengers => {
	const linkedPassengersByParentId = new Map<string, ExchangeTravellerValue[]>();

	passengers.forEach(passenger => {
		if (!passenger.linkedTraveller) {
			return;
		}

		const parentTravellerId = passenger.linkedTraveller.id;
		const linkedPassengers = linkedPassengersByParentId.get(parentTravellerId) ?? [];

		linkedPassengers.push(passenger);
		linkedPassengersByParentId.set(parentTravellerId, linkedPassengers);
	});

	return passengers
		.filter(passenger => !passenger.linkedTraveller)
		.map(traveller => {
			const linkedTravellers = linkedPassengersByParentId.get(traveller.id) ?? [];

			if (linkedTravellers.length) {
				return {
					ids: [traveller.id, ...linkedTravellers.map(linked => linked.id)],
					label: [traveller.values.fullName, ...linkedTravellers.map(linked => linked.values.fullName)].join(
						', '
					),
					isSelected: traveller.isSelected && linkedTravellers.every(linked => linked.isSelected)
				};
			}

			return {
				ids: [traveller.id],
				label: traveller.values.fullName,
				isSelected: traveller.isSelected
			};
		});
});

export const getSelectedPassengers = createSelector(getPassengers, passengers =>
	passengers.filter(passenger => passenger.isSelected)
);

export const getSelectedPassengersParams = createSelector(
	getSelectedPassengers,
	(selectedPassengers): PassengerTypeSearchParameter[] => {
		const passengersTypeMap = new Map<AviaPassengerType, number>();

		selectedPassengers.forEach(passenger => {
			const type = passenger.type as AviaPassengerType;

			passengersTypeMap.set(type, (passengersTypeMap.get(type) ?? 0) + 1);
		});

		return [...passengersTypeMap.entries()].map(([passengerType, count]) => ({
			passengerType,
			count
		}));
	}
);
